import { z } from 'zod';
import { DatasetType, ResourceType } from '../resource/resource.types';
import { editorStateSchema, llmModelIdSchema } from './editor-state';

/** Empty JSON object returned by mutation endpoints with no payload. */
export const emptyObjectResponseSchema = z.object({});

export const okTrueResponseSchema = z.object({
  ok: z.literal(true),
});

export const successBooleanResponseSchema = z.object({
  success: z.boolean(),
});

export const orgIdResponseSchema = z.object({
  orgId: z.string(),
});

export const okStringResponseSchema = z.literal('ok');

export const statusSuccessResponseSchema = z.object({
  status: z.literal('success'),
});

export const agentChatResponseSchema = z.object({
  success: z
    .boolean()
    .describe(
      'Whether the chat request was accepted and queued for processing.'
    ),
  conversationId: z
    .string()
    .uuid()
    .optional()
    .describe('Conversation UUID for the chat turn (new or existing).'),
});

export const agentChatBatchResponseSchema = z.object({
  success: z
    .boolean()
    .describe('Whether all batch chat requests were accepted.'),
  conversationIds: z
    .array(z.string().uuid())
    .describe(
      'Conversation UUID for each item in the batch, in request order.'
    ),
});

export const agentCompareResponseSchema = z.object({
  success: z
    .boolean()
    .describe('Whether all per-model chat requests were accepted.'),
  results: z
    .array(
      z.object({
        model: llmModelIdSchema,
        conversationId: z
          .string()
          .uuid()
          .describe('Conversation UUID started for this model.'),
      })
    )
    .describe(
      'One started conversation per requested model, in request order.'
    ),
});

export const agentResourceResponseSchema = z.object({
  id: z.string().uuid().describe('Agent UUID.'),
  orgId: z.string().uuid().describe('Owning organization UUID.'),
  ownerId: z.string().uuid().describe('User UUID that created/owns the agent.'),
  agentKey: z
    .string()
    .describe('Stable agent symbol/key used in workspace references.'),
  editorState: editorStateSchema,
  model: llmModelIdSchema,
  locked: z
    .boolean()
    .describe('Whether the agent is locked and cannot be edited.'),
  createdAt: z.string().datetime().describe('Creation timestamp (ISO 8601).'),
  updatedAt: z
    .string()
    .datetime()
    .describe('Last update timestamp (ISO 8601).'),
  title: z
    .string()
    .nullable()
    .describe('Display title derived from the first heading in editorState.'),
  description: z
    .string()
    .nullable()
    .describe('Optional description derived from editor content.'),
});

export const documentResourceResponseSchema = z.object({
  id: z.string().uuid().describe('Document UUID.'),
  orgId: z.string().uuid().describe('Owning organization UUID.'),
  ownerId: z
    .string()
    .uuid()
    .describe('User UUID that created/owns the document.'),
  editorState: editorStateSchema,
  title: z
    .string()
    .nullable()
    .describe('Display title derived from the first heading in editorState.'),
  description: z
    .string()
    .nullable()
    .describe('Optional description shown in the resource list.'),
  createdAt: z.string().datetime().describe('Creation timestamp (ISO 8601).'),
  updatedAt: z
    .string()
    .datetime()
    .describe('Last update timestamp (ISO 8601).'),
});

export const datasetArtifactMetadataResponseSchema = z.object({
  rowCount: z.number().int().nonnegative().nullable(),
  sourceRowCount: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional()
    .describe(
      'Rows the artifact was taken from. Equal to `rowCount` when the artifact holds the whole dataset, greater when it is a sample of one. Absent on artifacts written before it was recorded.'
    ),
  estimatedSizeBytes: z.number().int().nonnegative().nullable(),
  schema: z.object({
    columns: z.array(
      z.object({
        name: z.string(),
        type: z.string().nullable(),
      })
    ),
  }),
});

export const datasetArtifactResponseSchema = z.object({
  id: z.string().uuid().describe('Dataset artifact UUID.'),
  datasetId: z.string().uuid().describe('Parent dataset UUID.'),
  pointer: z.string().describe('Storage pointer for this artifact.'),
  hash: z.string().describe('Stable artifact identity hash.'),
  metadata: datasetArtifactMetadataResponseSchema.nullable().optional(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime().describe('Creation timestamp (ISO 8601).'),
  updatedAt: z
    .string()
    .datetime()
    .describe('Last update timestamp (ISO 8601).'),
  lastAccessedAt: z.string().datetime().nullable(),
});

/**
 * Per-column presentation the grid applies to a column's values. Pure display —
 * it never changes the stored rows — and shared across everyone who opens the
 * dataset. The shape mirrors the client data-grid's own display options so the
 * two can be seeded from and persisted to each other without a translation.
 */
export const datasetColumnDisplayOptionsSchema = z.object({
  numberFormat: z
    .enum(['number', 'percent', 'compact'])
    .describe('How a numeric value is rendered.'),
  decimals: z
    .number()
    .int()
    .min(0)
    .max(10)
    .nullable()
    .describe('Fixed decimal places, or null to keep the raw precision.'),
  alignment: z.enum(['left', 'center', 'right']),
  inCellBar: z
    .boolean()
    .describe('Draw a proportional bar behind numeric values.'),
  barMax: z
    .number()
    .optional()
    .describe('Upper bound for in-cell bars; absent lets the grid derive one.'),
});

export const datasetColumnSettingResponseSchema = z.object({
  columnName: z.string(),
  displayOptions: datasetColumnDisplayOptionsSchema,
});

export const datasetResourceResponseSchema = z.object({
  id: z.string().uuid().describe('Dataset UUID.'),
  orgId: z.string().uuid().describe('Owning organization UUID.'),
  ownerId: z
    .string()
    .uuid()
    .describe('User UUID that created/owns the dataset.'),
  type: z.enum(DatasetType),
  name: z.string().describe('Dataset display name.'),
  originalFilename: z.string().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  rowKeyColumns: z
    .array(z.string())
    .nullable()
    .optional()
    .describe(
      'Declared key columns, in order. Null falls back to the internal row id, which is not stable across a re-import — as does a declaration naming that row id outright, which is what a CSV dataset is given at import.'
    ),
  importStatus: z
    .enum(['importing', 'ready', 'failed'])
    .optional()
    .describe(
      'Where the dataset’s rows are in getting here. A CSV import completes after the request that started it, so `importing` means rows are still being read rather than absent.'
    ),
  importError: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Why the last import failed. Null unless importStatus is failed.'
    ),
  sourceQuery: z
    .string()
    .nullable()
    .optional()
    .describe(
      'The SELECT a warehouse dataset is seeded from. Null for kinds that source rows elsewhere.'
    ),
  checkingForChanges: z
    .boolean()
    .optional()
    .describe(
      'True while a source change-detection pass (a scheduled scan or a manual detect) is re-pulling the preview, so the grid can show a “checking for changes” indicator.'
    ),
  cadence: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Cron driving scheduled change detection. Null means push-only: the dataset is evaluated when a write announces a change, and for a warehouse dataset — which cannot announce one — never. Served so the schedule can be shown and edited after the dataset is created, not only chosen at it.'
    ),
  cadenceTimezone: z
    .string()
    .optional()
    .describe('IANA timezone the cadence cron is read in. Defaults to UTC.'),
  writeBackTable: z
    .object({ schema: z.string(), name: z.string() })
    .nullable()
    .optional()
    .describe(
      'The warehouse table workflow write-backs land in, when one was named. Null falls back to the table a plain single-table source query reads; a query with no single table (a join, an aggregate) writes nothing back until one is named.'
    ),
  preview: z
    .discriminatedUnion('queryable', [
      z.object({ queryable: z.literal(false) }),
      z.object({
        queryable: z.literal(true),
        query: z
          .string()
          .nullable()
          .describe(
            'Saved read-only SELECT used as the preview source, where `dataset` refers to the dataset itself. Null means the default.'
          ),
      }),
    ])
    .describe(
      'Preview capabilities for this dataset kind. Kinds that cannot be read through an ad-hoc SELECT report queryable: false and carry no query.'
    ),
  status: z.enum(['active', 'archived']),
  createdAt: z.string().datetime().describe('Creation timestamp (ISO 8601).'),
  updatedAt: z
    .string()
    .datetime()
    .describe('Last update timestamp (ISO 8601).'),
  expandedAutomationWorkflowIds: z
    .array(z.string())
    .nullable()
    .optional()
    .describe(
      'Ids of attached workflows the grid expands into a column showing each one’s most-recent run output for the row. Null or omitted means expand nothing; ids that no longer name a bound workflow are ignored.'
    ),
  artifacts: z.array(datasetArtifactResponseSchema).optional(),
  columnSettings: z
    .array(datasetColumnSettingResponseSchema)
    .optional()
    .describe(
      'Saved per-column display options, shared across everyone who opens the dataset. Only columns a user has customized appear here.'
    ),
  chatScoped: z
    .boolean()
    .optional()
    .describe(
      'True while the dataset is scoped to the chat that created it (from a file upload) and hidden from the library. Promoting it flips this to false.'
    ),
  sourceChatId: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe(
      'The chat this dataset was uploaded into, if any. Kept for provenance even after promotion.'
    ),
});

export const commitsCommitResponseSchema = z.object({
  commits: z.array(z.unknown()),
});

export const billingAddCardSessionResponseSchema = z.object({
  addCreditCardUrl: z.string(),
});

export const billingUpdateCardSessionResponseSchema = z.object({
  updateCreditCardUrl: z.string(),
});

export const toolsV2CreateAccountResponseSchema = z.object({
  id: z.string(),
  provider: z.string(),
  displayName: z.string(),
});

export const superAdminQueryResponseSchema = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
});

export const superAdminRestartFailuresResponseSchema = z.object({
  succeeded: z.number(),
  failed: z.number(),
  sampleError: z.string().optional(),
});

export const artifactsCreateResponseSchema = z.object({
  requestId: z.string(),
});

export const atcRunColumnValueResponseSchema = z.object({
  runId: z.string(),
  runKey: z.string(),
});

export const cliAuthPollTokenResponseSchema = z.union([
  z.object({ status: z.literal('pending') }),
  z.object({ token: z.string(), userId: z.string() }),
]);

export const cliAuthApproveResponseSchema = z.object({
  approved: z.boolean(),
  message: z.string().optional(),
  user_id: z.string().optional(),
  email: z.string().optional(),
});

export const folderOutputResponseSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  name: z.string(),
  position: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  items: z.array(z.unknown()),
  visibility: z.enum(['private', 'shared']),
  sharedBucket: z.enum(['org', 'external']).optional(),
});

export const folderItemsResponseSchema = z.object({
  items: z.array(z.unknown()),
  total: z.number(),
});

export const resourceFavoritesResponseSchema = z.object({
  items: z
    .array(z.unknown())
    .describe(
      'Favorites for the calling user, in their chosen order, as folder-content items. Favorites pointing at something they can no longer see are omitted.'
    ),
});

export const resourceListResponseSchema = z.object({
  type: z.nativeEnum(ResourceType),
  items: z.array(z.unknown()),
  total: z.number(),
});

export const resourceActivateShareLinkResponseSchema = z.object({
  mode: z.enum(['access', 'copy']),
  targets: z.array(
    z.object({
      type: z.nativeEnum(ResourceType),
      id: z.string().uuid(),
    })
  ),
  targetResourceType: z.nativeEnum(ResourceType),
  targetResourceId: z.string().uuid(),
  postActivationSequence: z.unknown().optional(),
});

export const toolsExecuteResponseSchema = z.object({
  type: z.literal('success'),
  data: z.unknown(),
});

export const toolsUpdateToolsConnectionResponseSchema = z.object({
  updatedTools: z.array(z.unknown()),
  count: z.number(),
  newConnectionId: z.string(),
});

export const warehouseConnectionResponseSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  displayName: z.string(),
  type: z.string(),
  writeSchema: z.string(),
  credentialsId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
